/**
 * Filter Validation Utilities
 *
 * Validates filters according to the Widget Data Filters API Contract.
 * Ensures filters are properly formatted before sending to the API.
 */

import type { QueryFilter, FilterOperator } from '../types/analytics';

/**
 * Valid operators according to the API contract
 */
export const VALID_OPERATORS: FilterOperator[] = [
  '=',
  '!=',
  '>',
  '<',
  '>=',
  '<=',
  'in',
  'like',
];

/**
 * Operators that require array values
 */
export const ARRAY_OPERATORS: FilterOperator[] = ['in'];

/**
 * Operators that require string values
 */
export const STRING_OPERATORS: FilterOperator[] = ['like'];

/**
 * Operators that require number or string values (e.g. date strings like "2024-01-01")
 */
export const COMPARISON_OPERATORS: FilterOperator[] = ['>', '<', '>=', '<='];

/**
 * Operators that require single (non-array) values
 */
export const SINGLE_VALUE_OPERATORS: FilterOperator[] = [
  '=',
  '!=',
  '>',
  '<',
  '>=',
  '<=',
  'like',
];

/**
 * Validation error types
 */
export interface FilterValidationError {
  field: string;
  operator: string;
  message: string;
}

/**
 * Validation result
 */
export interface FilterValidationResult {
  valid: boolean;
  errors: FilterValidationError[];
  cleanedFilters: QueryFilter[];
}

/**
 * Validates a single filter according to the API contract
 */
export function validateFilter(filter: QueryFilter): string | null {
  // Check operator is valid
  if (!VALID_OPERATORS.includes(filter.operator as FilterOperator)) {
    return `Unsupported filter operator: ${filter.operator}`;
  }

  const operator = filter.operator as FilterOperator;

  // Check array operators have array values
  if (ARRAY_OPERATORS.includes(operator)) {
    if (!Array.isArray(filter.value)) {
      return `'${operator}' operator requires array value, got ${typeof filter.value}`;
    }
    if (filter.value.length === 0) {
      return `'${operator}' operator requires non-empty array`;
    }
    // Check array contains valid types
    const invalidTypes = filter.value.filter(
      (v) => typeof v !== 'string' && typeof v !== 'number'
    );
    if (invalidTypes.length > 0) {
      return `'${operator}' operator array values must be strings or numbers`;
    }
  }

  // Check string operators have string values
  if (STRING_OPERATORS.includes(operator)) {
    if (typeof filter.value !== 'string') {
      return `'${operator}' operator requires string value, got ${typeof filter.value}`;
    }
  }

  // Check comparison operators have number or string values (strings for dates)
  if (COMPARISON_OPERATORS.includes(operator)) {
    if (typeof filter.value !== 'number' && typeof filter.value !== 'string') {
      return `'${operator}' operator requires number or string value, got ${typeof filter.value}`;
    }
  }

  // Check single value operators don't have arrays
  if (SINGLE_VALUE_OPERATORS.includes(operator)) {
    if (Array.isArray(filter.value)) {
      return `'${operator}' operator requires single value, got array. Use 'in' operator for multiple values.`;
    }
  }

  // Check field is not empty
  if (!filter.field || filter.field.trim() === '') {
    return 'Filter field cannot be empty';
  }

  // Check value is not null/undefined (unless it's a null check operator)
  if (filter.value === null || filter.value === undefined) {
    if (operator !== 'is_null' && operator !== 'is_not_null') {
      return 'Filter value cannot be null or undefined';
    }
  }

  return null;
}

/**
 * Validates an array of filters
 */
export function validateFilters(filters: QueryFilter[]): FilterValidationResult {
  const errors: FilterValidationError[] = [];
  const cleanedFilters: QueryFilter[] = [];

  filters.forEach((filter) => {
    const error = validateFilter(filter);
    if (error) {
      errors.push({
        field: filter.field,
        operator: filter.operator,
        message: error,
      });
    } else {
      cleanedFilters.push(filter);
    }
  });

  return {
    valid: errors.length === 0,
    errors,
    cleanedFilters,
  };
}

/**
 * Cleans filters according to best practices
 */
export function cleanFilters(filters: QueryFilter[]): QueryFilter[] {
  if (!filters || filters.length === 0) {
    return [];
  }

  const cleaned: QueryFilter[] = [];

  filters.forEach((filter) => {
    // Skip invalid filters
    if (validateFilter(filter)) {
      return;
    }

    // Optimize: Convert single-value 'in' arrays to '=' operator
    if (filter.operator === 'in' && Array.isArray(filter.value) && filter.value.length === 1) {
      cleaned.push({
        field: filter.field,
        operator: '=',
        value: filter.value[0],
      });
      return;
    }

    // Keep valid filter as-is
    cleaned.push(filter);
  });

  return cleaned;
}

/**
 * Checks if filters array should be omitted from request
 */
export function shouldOmitFilters(filters?: QueryFilter[]): boolean {
  return !filters || filters.length === 0;
}

/**
 * Prepares filters for API request
 */
export function prepareFiltersForRequest(filters?: QueryFilter[]): QueryFilter[] | undefined {
  if (shouldOmitFilters(filters)) {
    return undefined;
  }

  const cleaned = cleanFilters(filters!);

  if (cleaned.length === 0) {
    return undefined;
  }

  // Final validation
  const validation = validateFilters(cleaned);
  if (!validation.valid) {
    console.warn('⚠️ Filter validation errors:', validation.errors);
  }

  return cleaned;
}

/**
 * Type guard to check if operator requires array value
 */
export function requiresArrayValue(operator: FilterOperator): boolean {
  return ARRAY_OPERATORS.includes(operator);
}

/**
 * Type guard to check if operator requires string value
 */
export function requiresStringValue(operator: FilterOperator): boolean {
  return STRING_OPERATORS.includes(operator);
}

/**
 * Type guard to check if operator requires number value
 */
export function requiresNumberValue(operator: FilterOperator): boolean {
  return COMPARISON_OPERATORS.includes(operator);
}
